/* -----------------UI--------------- */
import {
	Stack,
	useFocusEffect,
	useGlobalSearchParams,
	useLocalSearchParams,
} from "expo-router";

/* -----------------Helpers & Hooks--------------- */
import { useAppStore } from "@hooks/useAppStore";
import { useConsent } from "@hooks/useConsent";
import { Redirect } from "expo-router";

/* -----------------Child components--------------- */

import EmailContainer from "@features/auth/containers/EmailContainer";
import { AppBar } from "@features/dashboard/layouts/AppBar";
import { buildType } from "@helpers/app-identity";
import { handleNavigateBack } from "@helpers/common";
import { replayManager } from "@helpers/lib/sentry";
import { captureErrorToSentry } from "@helpers/sentry-logger";
import { isNonActionableAuthError } from "@hooks/useClerkAuth/helpers";
import { useGlobalStore } from "@hooks/useGlobalStore";
import { useToast } from "@memorang/ui/components/Toast";
import { useDevice } from "@memorang/ui/useDevice";
/* -----------------Analytics--------------- */
import { trackCustomEvent } from "analytics";
import DocumentTitle from "components/DocumentTitle";
import { useCallback } from "react";

const LoginPage = () => {
	useFocusEffect(
		useCallback(() => {
			replayManager().startReplay();
		}, []),
	);

	const companyName = useAppStore((store) => store.companyName);
	const supportEmail = useAppStore((store) => store.supportEmail);
	const baseUrl = useAppStore((store) => store.app.links.baseUrl);
	const app = useAppStore((store) => store.app);
	const tenant = useAppStore((store) => store.tenant);
	const tos = useAppStore((store) => store.app.links.tos);
	const privacy = useAppStore((store) => store.app.links.privacy);
	const currentAppVersion = useAppStore((state) => state.app.version);
	const { isMobile } = useDevice();
	const { showToast } = useToast();
	const { requiresExplicitConsent } = useConsent();
	const pendingConsent = useGlobalStore((state) => state.pendingConsent);

	const appId = app.id;
	const tenantId = tenant.id;

	const lastLoggedInEmail = useGlobalStore((state) => state.lastLoggedInEmail);
	const params = useLocalSearchParams<{
		redeemCode: string;
		email: string;
	}>();

	const isTokenBasedFlow = useAppStore(
		(store) => store.app.auth?.tokenBasedFlow ?? false,
	);

	const { auth_token } = useGlobalSearchParams<{ auth_token: string }>();
	const isDeeplinkFlow = !!auth_token || !!params?.redeemCode;
	// useEffect(() => {
	// 	trackPageView({ type: pageViewTypes.login });
	// }, []);

	const title = "Continue with email";

	const emailFromUrl = params?.email
		? decodeURIComponent(params?.email).replace(/ /g, "+")
		: "";

	const onError = (message: string) => {
		showToast({ message });
	};

	const logError = (error: unknown, title: string) => {
		if (isNonActionableAuthError(error)) {
			return;
		}

		const errorObj = new Error(title);
		captureErrorToSentry(errorObj, {
			error: JSON.stringify(error),
		});
	};

	if (isTokenBasedFlow) {
		return <Redirect href="/(auth)/auth/unauthorized" />;
	}

	if (requiresExplicitConsent && !pendingConsent && !isDeeplinkFlow) {
		return <Redirect href="/" />;
	}

	return (
		<>
			<Stack.Screen
				options={{
					headerShown: true,
					header: () => {
						return (
							<AppBar
								variant="public"
								showLogo={!isMobile}
								title={title}
								onPressBack={() => handleNavigateBack("/")}
							/>
						);
					},
				}}
			/>
			<DocumentTitle title="Login" />
			<EmailContainer
				handleTracking={trackCustomEvent}
				companyName={companyName}
				lastLoggedInEmail={lastLoggedInEmail}
				supportEmail={supportEmail}
				buildType={buildType}
				appId={appId}
				tenantId={tenantId}
				redirectUrl={`${baseUrl}/verify`}
				currentAppVersion={currentAppVersion!}
				emailFromUrl={emailFromUrl}
				tos={tos}
				privacy={privacy}
				logError={logError}
				onError={onError}
			/>
		</>
	);
};

export default LoginPage;
